import { ReactElement, useCallback, useMemo } from 'react';
import UnlockModelDialog from '@casops/dialogs/unlock-model-dialog/UnlockModelDialog';
import { ModelName } from '@casops/store/api';
import { ModelWithId } from '@casops/store/apis/commonModels';
import { useTranslation } from 'react-i18next';
import { ModelUnlockProps } from '../../ModelActionsCellRenderer';

interface UnlockActionProps<TData extends ModelWithId, TQueryArg = ModelWithId>
  extends ModelUnlockProps<TData, TQueryArg> {
  data: TData;
  modelName: ModelName;
  isIconButton?: boolean;
  navigateTo?: string;
}

export const UnlockAction = <TData extends ModelWithId, TQueryArg = ModelWithId>({
  data,
  modelName,
  canUnlockItem,
  getItemName,
  getQueryArg,
  useUnlockModelMutation,
  isIconButton,
  navigateTo,
}: UnlockActionProps<TData, TQueryArg>): ReactElement => {
  const { t } = useTranslation();

  const isUnlockItemAllowed = useMemo(() => {
    if (data) {
      return typeof canUnlockItem === 'function' ? canUnlockItem(data) : canUnlockItem;
    }
    return false;
  }, [data, canUnlockItem]);

  const itemName = data ? getItemName(data) : t('Unknown');

  const [unlockModel, { error, isLoading }] = useUnlockModelMutation();

  // Create an async callback that populates the patch mutation trigger with arguments from the row data.
  const unlockModelCallback = useCallback(async (): Promise<void> => {
    return await unlockModel(getQueryArg(data)).unwrap();
  }, [unlockModel, data, getQueryArg]);

  return (
    <UnlockModelDialog
      itemName={itemName}
      modelName={modelName}
      disabled={!isUnlockItemAllowed}
      unlockModelCallback={unlockModelCallback}
      error={error}
      isLoading={isLoading}
      isIconButton={isIconButton}
      navigateTo={navigateTo}
    />
  );
};

export default UnlockAction;
